import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Legend,
} from "recharts";

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const sentiment = payload.find((p) => p.dataKey === "sentiment")?.value;
  const price = payload.find((p) => p.dataKey === "price")?.value;
  const change = payload.find((p) => p.dataKey === "sentiment")?.payload?.change_pct;
  const c = sentiment < 30 ? "#FF3B30" : sentiment < 70 ? "#FFCC00" : "#34C759";
  return (
    <div className="bg-black border border-white/15 px-3 py-2" style={{ minWidth: 160 }}>
      <div className="overline mb-1">{label}</div>
      {price !== undefined && (
        <div className="mono text-xs">price <span className="text-white/60 ml-2">{Number(price).toFixed(2)}</span></div>
      )}
      {sentiment !== undefined && (
        <div className="mono text-xs" style={{ color: c }}>sentiment <span className="ml-2">{Math.round(sentiment)}</span></div>
      )}
      {change !== undefined && (
        <div className="mono text-xs">change <span className={`ml-2 ${change >= 0 ? "text-bullish" : "text-bearish"}`}>{change >= 0 ? "+" : ""}{Number(change).toFixed(2)}%</span></div>
      )}
    </div>
  );
}

export default function SentimentBacktestChart({ series = [], height = 320 }) {
  if (!series || series.length === 0) {
    return (
      <div className="h-72 flex items-center justify-center overline" data-testid="backtest-chart-empty">
        no historical data
      </div>
    );
  }
  // Sample down if too dense
  const step = series.length > 240 ? Math.ceil(series.length / 240) : 1;
  const data = step > 1 ? series.filter((_, i) => i % step === 0) : series;

  return (
    <div className="w-full" data-testid="backtest-chart">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 56, left: 0, bottom: 8 }}>
          <defs>
            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A1A1AA" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#A1A1AA" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="0" />
          {/* Sentiment zones as backdrop */}
          <ReferenceArea y1={0} y2={30} yAxisId="sentiment" fill="#FF3B30" fillOpacity={0.05} />
          <ReferenceArea y1={30} y2={70} yAxisId="sentiment" fill="#FFCC00" fillOpacity={0.04} />
          <ReferenceArea y1={70} y2={100} yAxisId="sentiment" fill="#34C759" fillOpacity={0.05} />
          <ReferenceLine y={50} yAxisId="sentiment" stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }}
            tickLine={false}
            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            minTickGap={32}
          />
          <YAxis
            yAxisId="sentiment"
            domain={[0, 100]}
            tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }}
            tickLine={false}
            axisLine={false}
            width={40}
            label={{ value: "sentiment", angle: -90, position: "insideLeft", fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono", letterSpacing: "0.18em" }}
          />
          <YAxis
            yAxisId="price"
            orientation="right"
            domain={["auto", "auto"]}
            tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "IBM Plex Mono" }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(255,255,255,0.15)" }} />
          <Legend
            iconType="plainline"
            wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#A1A1AA" }}
          />
          <Area
            yAxisId="price"
            type="monotone"
            dataKey="price"
            name="price"
            stroke="#A1A1AA"
            strokeWidth={1.2}
            fill="url(#priceGrad)"
            dot={false}
            isAnimationActive={false}
          />
          <Line
            yAxisId="sentiment"
            type="monotone"
            dataKey="sentiment"
            name="sentiment"
            stroke="#FFCC00"
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
